// Auto-restock, a VIP perk (Cole, 2026-10-19): while VIP runs, the hunt tops seals, potions and Revives back up to
// the counts the player set, at the Merchant's normal price, with coins only. It never changes odds and never
// catches; a player without VIP stocks up by hand before going AFK, as before. Off until the player turns it on.
import { ITEM, ITEMS, shopPriceAt } from './items.ts';
import type { TrainerDTO } from './protocol.ts';

export const RESTOCK = {
  /** How often the hunt checks the bag (hunt clock). */
  everyMs: 10_000,
  /** Most a player can ask to keep of one item. */
  maxKeep: 9999,
  /** One restock never spends more than this share of the coins above the floor. */
  spendShare: 0.8,
  /** First switch-on with nothing set: keep this many of the first seal and potion in use (or what the bag holds, if more). */
  firstSeals: 100,
  firstPotions: 10,
} as const;

/** The keep counts a first switch-on starts from. */
export function firstKeep(t: Pick<TrainerDTO, 'settings' | 'bag'>): Record<string, number> {
  const keep: Record<string, number> = {};
  for (const id of RESTOCK_ITEMS) if ((t.bag[id] ?? 0) > 0) keep[id] = t.bag[id];
  const seal = t.settings.seals.find((r) => r.on)?.id, potion = t.settings.potions.find((r) => r.on)?.id;
  if (seal && RESTOCK_ITEMS.includes(seal)) keep[seal] = Math.max(keep[seal] ?? 0, RESTOCK.firstSeals);
  if (potion && RESTOCK_ITEMS.includes(potion)) keep[potion] = Math.max(keep[potion] ?? 0, RESTOCK.firstPotions);
  return keep;
}

/** What auto-restock can buy: the Merchant's seals, potions and Revives. */
export const RESTOCK_ITEMS: string[] = ITEMS.filter((i) => (i.kind === 'seal' || i.kind === 'potion' || i.kind === 'revive') && i.shopPrice).map((i) => i.id);

export interface RestockSettings {
  on: boolean;
  /** Item id -> how many to keep in the bag (0 or absent = don't buy it). */
  keep: Record<string, number>;
  /** Never spend below this many coins. */
  floor?: number;
}

export const vipOn = (t: Pick<TrainerDTO, 'boosts'>, now: number) => t.boosts.vipUntil > now;

/** Clean a restock setting from the client; null when it isn't one. */
export function cleanRestock(v: unknown): RestockSettings | null {
  if (!v || typeof v !== 'object') return null;
  const r = v as Partial<RestockSettings>;
  const keep: Record<string, number> = {};
  if (r.keep && typeof r.keep === 'object') {
    for (const id of RESTOCK_ITEMS) {
      const n = Math.floor(Number((r.keep as Record<string, unknown>)[id]));
      if (Number.isFinite(n) && n > 0) keep[id] = Math.min(RESTOCK.maxKeep, n);
    }
  }
  const floor = Math.max(0, Math.floor(Number(r.floor) || 0));
  return { on: r.on === true, keep, ...(floor ? { floor } : {}) };
}

/**
 * What a restock buys now, in the order of RESTOCK_ITEMS (Revives last, so cheap seals and potions come first).
 * Empty without VIP, with the setting off, or when nothing is short.
 */
export function restockPlan(t: Pick<TrainerDTO, 'boosts' | 'settings' | 'bag' | 'coins' | 'level'>, now: number): { id: string; qty: number; cost: number }[] {
  const r = t.settings.restock;
  if (!r?.on || !vipOn(t, now)) return [];
  let budget = Math.floor(Math.max(0, t.coins - (r.floor ?? 0)) * RESTOCK.spendShare);
  const out: { id: string; qty: number; cost: number }[] = [];
  for (const id of RESTOCK_ITEMS) {
    const want = (r.keep[id] ?? 0) - (t.bag[id] ?? 0);
    const price = shopPriceAt(id, t.level);
    if (want <= 0 || !price || !ITEM[id]) continue;
    const qty = Math.min(want, Math.floor(budget / price));
    if (qty <= 0) continue;
    out.push({ id, qty, cost: qty * price });
    budget -= qty * price;
  }
  return out;
}
